import { ContextualInfoButtonWithText } from "@/components/buttons/ContextualInfoButton";
import { SwitchWithLabel } from "@/components/form/SwitchWithLabels";
import { getHazardList } from "@/features/branding";
import { anaHazards } from "@/lib/crg_apis/constants/hazards";
import { GridItem } from "@/tools/aggregate/portfolio-export/components/PortfolioExportFormItems";
import { LargeSiteAdvancedSettingValues } from "@/tools/create/lss-from-map/types";
import type { RefSelectProps } from "antd";
import { Col, Collapse, Row, Select } from "antd";
import { Dispatch, MutableRefObject, SetStateAction, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
interface LargeSiteAdvancedSettingsProps {
  defaultOpen?: boolean;
  setRefs?: (refs: LargeSiteAdvancedSettingsRefs) => void;
  values: LargeSiteAdvancedSettingValues;
  setValues: Dispatch<SetStateAction<LargeSiteAdvancedSettingValues>>;
}
export interface LargeSiteAdvancedSettingsRefs {
  riskiestPointHazard: MutableRefObject<RefSelectProps | null>;
  riskiestPointYear: MutableRefObject<RefSelectProps | null>;
  /** Wraps the whole Riskiest Point Settings section (title + both selects). */
  riskiestPointSection: MutableRefObject<HTMLDivElement | null>;
  anomalyManagementRp: MutableRefObject<HTMLButtonElement | null>;
  defences: MutableRefObject<HTMLButtonElement | null>;
  /** Wraps both the Anomaly Management and Defences toggle boxes. */
  advancedTogglesSection: MutableRefObject<HTMLDivElement | null>;
}
export const LargeSiteAdvancedSettings = ({
  defaultOpen,
  values,
  setRefs,
  setValues
}: LargeSiteAdvancedSettingsProps) => {
  const {
    t
  } = useTranslation("tools", {
    keyPrefix: "settings"
  });
  const anomalyManagementRpRef = useRef<HTMLButtonElement>(null);
  const defencesRef = useRef<HTMLButtonElement>(null);
  const riskiestPointHazardRef = useRef<RefSelectProps>(null);
  const riskiestPointYearRef = useRef<RefSelectProps>(null);
  const riskiestPointSectionRef = useRef<HTMLDivElement>(null);
  const advancedTogglesSectionRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!setRefs) {
      return;
    }
    setRefs({
      riskiestPointHazard: riskiestPointHazardRef,
      riskiestPointYear: riskiestPointYearRef,
      riskiestPointSection: riskiestPointSectionRef,
      anomalyManagementRp: anomalyManagementRpRef,
      defences: defencesRef,
      advancedTogglesSection: advancedTogglesSectionRef
    });
  }, [anomalyManagementRpRef, defencesRef, riskiestPointYearRef, riskiestPointHazardRef]);
  return <Row gutter={[16, 0]} data-sentry-element="Row" data-sentry-component="LargeSiteAdvancedSettings" data-sentry-source-file="LargeSiteAdvancedSettings.tsx">
      <Col span={24} data-sentry-element="Col" data-sentry-source-file="LargeSiteAdvancedSettings.tsx">
        <Collapse size="small" activeKey={defaultOpen ? "advanced-settings" : undefined} className="rounded-lg border border-subtle bg-surface *:!border-subtle [&_.ant-collapse-content]:border-t-0 [&_.ant-collapse-panel]:!border-subtle" expandIconPlacement="start" items={[{
        key: "advanced-settings",
        label: t("advancedAnalysisSettings"),
        classNames: {
          body: "!pt-1 !border-subtle border-t-0"
        },
        children: <>
                  <div ref={riskiestPointSectionRef} className="mb-2 flex flex-col border-b border-subtle pb-2 pt-2 *:mb-2">
                    <ContextualInfoButtonWithText rootClassName="!inline-flex mr-2" id="riskiestPoint" size="small">
                      Riskiest Point Settings
                    </ContextualInfoButtonWithText>
                    <GridItem>
                      <div>Year of Interest</div>
                      <Select className="!col-span-2 w-full" options={[{
                value: 2025
              }, {
                value: 2050
              }, {
                value: 2100
              }]} ref={riskiestPointYearRef} defaultValue={values.riskiestPoint.year} onChange={value => setValues({
                ...values,
                riskiestPoint: {
                  ...values?.riskiestPoint,
                  year: value
                }
              })} />
                    </GridItem>

                    <GridItem>
                      <div>Hazard of Interest</div>
                      <Select className="!col-span-2 w-full" options={[{
                label: "All Hazards (Total)",
                value: "total"
              }, ...getHazardList().available.filter(hazard => hazard !== "heat") // Extreme Heat does not have damage values.
              .map(value => {
                const hazard = anaHazards[value];
                return {
                  label: hazard.name,
                  value
                };
              }).sort((a, b) => a.label > b.label ? 1 : -1)]} ref={riskiestPointHazardRef} defaultValue={values.riskiestPoint.hazard} onChange={value => setValues({
                ...values,
                riskiestPoint: {
                  ...values?.riskiestPoint,
                  hazard: value
                }
              })} />
                    </GridItem>
                  </div>
                  <div ref={advancedTogglesSectionRef} className="flex flex-col divide-y divide-subtle pt-2 *:py-4">
                    <SwitchWithLabel id="anomalyManagementRp" label={t("anomalyManagementRp.title")} description={t("anomalyManagementRp.description")} warning={t("anomalyManagementRp.warningIfFalse")} showWarning={!values?.anomalyManagementRp} ref={anomalyManagementRpRef} checked={values?.anomalyManagementRp} onChange={(checked: boolean) => {
              setValues({
                ...values,
                anomalyManagementRp: checked
              });
            }} />

                    <SwitchWithLabel id="defences" label={t("defences.title")} description={t("defences.description")} warning={t("defences.warningIfFalse")} showWarning={!values?.defences} ref={defencesRef} checked={values?.defences} onChange={(checked: boolean) => {
              setValues({
                ...values,
                defences: checked
              });
            }} />
                  </div>
                </>
      }]} data-sentry-element="Collapse" data-sentry-source-file="LargeSiteAdvancedSettings.tsx" />
      </Col>
    </Row>;
};